import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    ArrowLeft,
    Package,
    Check,
    RefreshCw,
    Mail,
    Layers,
    Target,
    Award,
    Boxes,
    Sparkles,
} from "lucide-react";

import SidebarLayout from "@/components/SidebarLayout";
import { api } from "@/lib/api";

const KIND_META = {
    email_templates: { icon: Mail, label: "Email Templates", href: "/outreach/templates" },
    outreach_sequences: { icon: Layers, label: "Sequence", href: "/outreach/sequences" },
    lead_searches: { icon: Target, label: "Lead search", href: "/leads" },
    user_badges: { icon: Award, label: "Badge", href: "/civilization/badges" },
    user_module_access: { icon: Boxes, label: "Module unlocked", href: "/modules" },
    user_agent_configs: { icon: Sparkles, label: "Agent config", href: "#" },
};

export default function MarketplaceLibrary() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await api.marketplaceLibrary();
            setItems(d.items || []);
        } catch (e) {
            toast.error(e.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    async function reapply(id) {
        try {
            await api.marketplaceReapply(id);
            toast.success("Reapplied to your workspace");
            await load();
        } catch (e) {
            toast.error(e.message || "Reapply failed");
        }
    }

    return (
        <SidebarLayout>
            <div className="mx-auto max-w-5xl">
                <Link
                    to="/marketplace/v10"
                    className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-text-main mb-4"
                    data-testid="library-back"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to marketplace
                </Link>

                <motion.header
                    initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                    className="mb-6"
                >
                    <p className="text-[10px] uppercase tracking-widest text-text-secondary">Library</p>
                    <h1 className="text-3xl md:text-4xl font-semibold mt-1">
                        Your marketplace assets
                    </h1>
                    <p className="text-text-secondary mt-2 text-sm">
                        Everything you've unlocked. Applied entities live in their native workspace section.
                    </p>
                </motion.header>

                {loading ? (
                    <div className="text-text-secondary text-sm">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-10 text-center">
                        <Package className="h-8 w-8 mx-auto text-text-secondary" />
                        <p className="mt-3 text-text-secondary">
                            Your library is empty.{" "}
                            <Link to="/marketplace/v10" className="text-[var(--color-secondary)]">
                                Browse the marketplace →
                            </Link>
                        </p>
                    </div>
                ) : (
                    <div data-testid="library-grid" className="grid gap-4 md:grid-cols-2">
                        {items.map((p) => {
                            const meta = KIND_META[p.applied_kind] || { icon: Package, label: p.applied_kind, href: "#" };
                            const Icon = meta.icon;
                            return (
                                <div
                                    key={p.id}
                                    data-testid={`library-item-${p.asset_slug}`}
                                    className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="h-10 w-10 rounded-xl bg-[var(--color-primary)]/15 grid place-items-center text-[var(--color-primary)] shrink-0">
                                            <Icon className="h-5 w-5" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-[10px] uppercase tracking-widest text-text-secondary">
                                                {meta.label}
                                            </p>
                                            <h3 className="font-medium truncate">{p.asset?.name || p.asset_slug}</h3>
                                            <p className="text-xs text-text-secondary mt-1">
                                                Purchased {new Date(p.purchased_at).toLocaleDateString()} · {p.price_credits_paid} credits
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mt-3 flex items-center gap-2">
                                        {p.applied ? (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-success)]/10 border border-[var(--color-success)]/40 px-2.5 py-1 text-[11px] text-[var(--color-success)]">
                                                <Check className="h-3 w-3" /> Applied
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-alert)]/10 border border-[var(--color-alert)]/40 px-2.5 py-1 text-[11px] text-[var(--color-alert)]">
                                                Not applied
                                            </span>
                                        )}
                                        {p.applied_kind === "user_module_access" && (
                                            <span className="text-[11px] text-text-secondary">
                                                Unlocked permanently
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-4 flex items-center gap-2">
                                        {meta.href !== "#" && (
                                            <Link
                                                to={meta.href}
                                                data-testid={`library-open-${p.asset_slug}`}
                                                className="rounded-full bg-[var(--color-primary)]/15 text-[var(--color-primary)] px-3 py-1.5 text-xs"
                                            >
                                                View in {meta.label} →
                                            </Link>
                                        )}
                                        <button
                                            onClick={() => reapply(p.id)}
                                            data-testid={`library-reapply-${p.asset_slug}`}
                                            className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] px-3 py-1.5 text-xs text-text-secondary hover:text-text-main"
                                        >
                                            <RefreshCw className="h-3 w-3" />
                                            Reapply
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </SidebarLayout>
    );
}
